import { isEmpty } from 'lodash';
import { usePathname } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { useSnapshot } from 'valtio';
import api from '@/api';
import { initBasicLayers, setPatternLoaded } from '@/components/MapGeneration/layers';
import { setCurrentDate, setCurrentRequiredInjection } from '@/components/MapGeneration/store/actions';
import { state } from '@/components/MapGeneration/store/state';
import {
    baseProgress,
    Cmd,
    coordProgress,
    debitProgress,
    gtmProgress,
    stateProgress,
    transitProgress,
    voronoiProgress,
} from '@/components/wasm/lib';
import Spin from '@/shared/Spin';
import { createQtLoader } from './utils';
import { api as apiV2 } from '@/api/api.v2';
import { encode } from 'cbor-x';
import { Base64 } from 'js-base64';
import { useDispatch } from 'react-redux';
import { useItemParams } from '@/components/MiningAndPumping/MiniMapWidget/Toolbar/ProjectFondsSettings/utils';
import { useMessage } from '@/providers/MessagesProvider/hooks';
import { useSelector } from 'react-redux';

const getMonthTimestamps = date => [
    new Date(new Date(date.getFullYear(), date.getMonth(), 1)).getTime(),
    new Date(new Date(date.getFullYear(), date.getMonth() + 1, 0)).getTime(),
];

export const STATES = {
    1: 'Добывающая',
    7: 'Нагнетательная',
    21: 'Возвраты',
    22: 'Разведочная добывающая',
    34: 'Водозаборная в работе',
    40: 'Газовая в работе',
    46: 'Газоконденсантная в работе',
    52: 'Поглощающая в работе',
    58: 'Специальная в работе',
};

export const updateWells = async projectFundIds => {
    await Cmd.exec('clear_well_design_stock');

    const { projectFundWells, projectFundWellsMap } = await apiV2.map.projectFund.allWells();
    const wellTypes = await apiV2.map.projectFund.wellTypes();
    const states = {};

    for (const wellType of wellTypes) {
        states[wellType.id] = wellType.title;
    }

    const filteredFunds = Object.entries(projectFundWellsMap).filter(([id]) => projectFundIds.includes(Number(id)));

    const design_stocks = filteredFunds.map(([id, { wells, color }]) => ({
        id: Number(id),
        color: color ?? '#000000',
        wells: wells.filter(Boolean),
    }));

    const selectedWellIds = design_stocks.flatMap(f => f.wells);
    const filteredWells = projectFundWells.filter(w => selectedWellIds.includes(w.id));

    const fundWellsWasm = {
        states,
        wells: filteredWells.map(
            ({ id, wellNumber, stratumIntersectX, stratumIntersectY, developmentIconId, bottomHoleX, bottomHoleY }) =>
                bottomHoleX && bottomHoleY
                    ? {
                          id,
                          name: wellNumber,
                          state: developmentIconId,
                          x: stratumIntersectX + 1e-3,
                          y: stratumIntersectY + 1e-3,
                          last_point_x: bottomHoleX + 1e-3,
                          last_point_y: bottomHoleY + 1e-3,
                      }
                    : {
                          id,
                          name: wellNumber,
                          state: developmentIconId,
                          x: stratumIntersectX + 1e-3,
                          y: stratumIntersectY + 1e-3,
                      },
        ),
        design_stocks,
    };
    console.log(fundWellsWasm);

    await Cmd.exec('parse_well_design_stock_cbor', Base64.fromUint8Array(encode(fundWellsWasm)));
    await Cmd.exec('update_plot');
};

export const updateVisibility = async selectedProjectFonds => {
    const { projectFundWellsMap } = await apiV2.map.projectFund.allWells();

    const allProjectFundIds = Object.keys(projectFundWellsMap).map(Number);

    const invisibleProjectFundIds = allProjectFundIds.filter(id => !selectedProjectFonds.includes(id));
    console.log(invisibleProjectFundIds);

    await Cmd.exec('set_layer_settings', {
        id: 'well_design_stock_1',
        settings: {
            invisibleStocks: invisibleProjectFundIds,
        },
    });

    await Cmd.exec('update_plot');
};

export const MapView = () => {
    const [loading, setLoading] = useState(false);

    const $state = useSnapshot(state);

    const $baseProgress = useSnapshot(baseProgress);
    const $coordProgress = useSnapshot(coordProgress);
    const $gtmProgress = useSnapshot(gtmProgress);
    const $transitProgress = useSnapshot(transitProgress);
    const $stateProgress = useSnapshot(stateProgress);
    const $debitProgress = useSnapshot(debitProgress);
    const $voronoiProgress = useSnapshot(voronoiProgress);

    const dispatch = useDispatch();
    const { setOpenCreateWellModal, setAddedPoint } = dispatch.projectFund;

    const pathname = usePathname();
    const params = useItemParams();
    const paramsRef = useRef(params);

    useEffect(() => {
        paramsRef.current = params;
    }, [params]);

    const messageApi = useMessage();
    const { selectedProjectFonds, projectFondsData } = useSelector(state => state.projectFund);
    const projectIds = projectFondsData.map(({ id }) => id);

    useEffect(() => {
        if (!selectedProjectFonds) return;
        updateVisibility(selectedProjectFonds);
    }, [selectedProjectFonds]);

    const loadWASM = async () => {
        window.is_dark_theme = () => {
            const htmlElement = document.documentElement;
            return htmlElement.classList.contains('dark') ? 1 : 0;
        };

        window.on_command_executed = () => {
            //console.log(e);
        };

        // Hook for commands: set_ruler_active, set_lasso_active, set_circle_active
        window.on_wells_selected = msg => {
            console.info(msg);
            const result = JSON.parse(msg);
            state.selectedWells = result.map(({ well_id }) => well_id);
            state.checkedSelectedWells = result.reduce((acc, { well_id }) => ({ ...acc, [well_id]: true }), {});
        };

        window.on_mouse_clicked = msg => {
            const currentParams = paramsRef.current;

            if (Object.values(currentParams).every(v => !v)) {
                messageApi.warning('Выберите месторождение');
            } else {
                setAddedPoint(JSON.parse(msg));
                setOpenCreateWellModal(true);
            }
        };

        window.on_scale_changed = msg => {
            state.scale = msg;
        };

        window.on_user_circle_modified = circle => {
            state.selectedUserCircle = JSON.parse(circle);
        };

        window.on_layer_callback = msg => {
            console.log('on_layer_callback fired at', performance.now());
            state.isVoronoiReady = Boolean(msg);
        };

        const scriptId = 'qtloader-script';
        const existingScript = document.getElementById(scriptId);
        if (!existingScript) {
            const script = document.createElement('script');
            script.src = '/wasm/minimap/qtloader.js';
            script.async = true;
            script.id = scriptId;
            document.body.appendChild(script);

            script.onload = () => {
                const canvas = document.querySelector('#scene');
                createQtLoader(canvas, '/wasm/minimap/arm_map_app', {
                    onShowLoader: () => setLoading(true),
                    onShowError: () => setLoading(false),
                    onShowExit: () => setLoading(false),
                    onShowCanvas: () => setLoading(false),
                });
            };
        } else {
            console.info('qtloader.js already loaded');

            if (window.QtLoader) {
                const canvas = document.querySelector('#scene');
                createQtLoader(canvas, '/wasm/minimap/arm_map_app', {
                    onShowLoader: () => setLoading(true),
                    onShowError: () => setLoading(false),
                    onShowExit: () => setLoading(false),
                    onShowCanvas: () => setLoading(false),
                });
            }
        }
    };

    useEffect(() => {
        loadWASM();
    }, []);

    useEffect(() => {
        if (state.appendNewField === true) {
            state.appendNewField = false;

            const init = async () => {
                if (isEmpty(state.fields)) {
                    return;
                }

                if (state.layerStructure.size === 0) {
                    initBasicLayers();
                }

                const [dateFrom, dateTo] = getMonthTimestamps(state.currentDate);

                onUserCircleLoad();
                // WellsLoader
                baseProgress.setLoading(true);
                coordProgress.setLoading(true);
                const rawWellsPreResponse = await api.mapGeneration.fetchWellByNode({
                    ...state.requestWells,
                    versions: state.versions,
                    cbor: true,
                    only_filename: true,
                    base64: true,
                });
                const rawWellsPreResponseJson = await rawWellsPreResponse.json();

                let rawWells;
                if (rawWellsPreResponseJson.mediafile) {
                    rawWells = await api.mapGeneration.fetchWellByNodeByMedia(rawWellsPreResponseJson.mediafile);
                } else {
                    rawWells = await api.mapGeneration.fetchWellByNode({
                        ...state.requestWells,
                        versions: state.versions,
                        cbor: true,
                        base64: true,
                    });
                }

                await Cmd.parseStreamCBOR('parse_field_wells_cbor', rawWells, true);

                await Cmd.exec('reset_plot');
                await Cmd.exec('fit_plot');
                await Cmd.exec('update_plot');
                onWellsLoad();
                coordProgress.setLoading(false);

                // ProductionLoader
                debitProgress.setLoading(true);
                const rawDebitZIP = await api.mapGeneration.fetchProductionZIP_v2({
                    ...state.requestWells,
                    versions: state.versions ? state.versions : [0],
                    minDate: Math.floor(dateFrom / 1000),
                    maxDate: Math.floor(dateTo / 1000),
                    cbor: true,
                    chunkSize: 10,
                    accum: false,
                });
                const debitFiles = await Cmd.unZIP(rawDebitZIP);

                for (let i = 0; i < debitFiles.length; ++i) {
                    requestIdleCallback(
                        async () =>
                            await Cmd.parseFilesCBOR(
                                'parse_map_production_cbor',
                                debitFiles[i]._data.compressedContent,
                            ),
                        { timeout: 16 },
                    );
                }
                await Cmd.exec('update_plot');
                await onProductionLoad();

                // GtmLoader
                gtmProgress.setLoading(true);
                // const rawGtm = await api.mapGeneration.fetchGtmCBOR(state.fields, true);
                const { stratums: gtmStratums, fields: gtmFields, ...gtmRequest } = state.requestWells;
                const rawGtm = await api.mapGeneration.fetchGtmCBOR(
                    { ...gtmRequest, stratumIds: gtmStratums, fieldIds: gtmFields },
                    true,
                );
                await Cmd.parseStreamCBOR('parse_well_gtm_cbor', rawGtm);
                await Cmd.exec('update_plot');
                await onGtmLoad();

                // TransitLoader
                transitProgress.setLoading(true);

                const rawTransitZIPold = await api.mapGeneration.fetchProductionTransitZIPold({
                    ...state.requestWells,
                    versions: state.versions,
                    minDate: Math.floor(dateFrom / 1000),
                    maxDate: Math.floor(dateTo / 1000),
                    cbor: true,
                });
                const transitFilesOld = await Cmd.unZIP(rawTransitZIPold);
                for (let i = 0; i < transitFilesOld.length; ++i) {
                    requestIdleCallback(
                        async () =>
                            await Cmd.parseFilesCBOR(
                                'parse_well_transit_cbor',
                                transitFilesOld[i]._data.compressedContent,
                            ),
                        { timeout: 16 },
                    );
                }

                const rawTransitZIP = await api.mapGeneration.fetchProductionTransitZIP({
                    ...state.requestWells,
                    versions: state.versions,
                    minDate: Math.floor(dateFrom / 1000),
                    maxDate: Math.floor(dateTo / 1000),
                    cbor: true,
                });
                const transitFiles = await Cmd.unZIP(rawTransitZIP);
                for (let i = 0; i < transitFiles.length; ++i) {
                    requestIdleCallback(
                        async () =>
                            await Cmd.parseFilesCBOR(
                                'parse_well_transit_v3_cbor',
                                transitFiles[i]._data.compressedContent,
                            ),
                        { timeout: 16 },
                    );
                }
                await Cmd.exec('update_plot');
                await onTransitLoad();

                // WellStateLoader
                stateProgress.setLoading(true);
                const { fields: rawFields, ...rawWellsRequest } = state.requestWells;
                const rawWellState = await api.mapGeneration.fetchStatesCBOR(
                    { ...rawWellsRequest, fieldIds: rawFields },
                    true,
                );
                await Cmd.parseStreamCBOR('parse_well_state_cbor', rawWellState);
                await Cmd.exec('update_plot');
                onStatesLoad();

                // ContourLoader
                const { stratums: contourStratums, fields: contourFields, ...rawContoursRequest } = state.requestWells;
                const rawContours = await api.mapGeneration.fetchContoursCBOR(
                    {
                        ...rawContoursRequest,
                        fieldIds: contourFields,
                        stratumIds: contourStratums,
                        username: 'skorskin.ss',
                    },
                    true,
                );
                await Cmd.parseStreamCBOR('parse_contour_cbor', rawContours);
                await Cmd.exec('update_plot');

                await onContourLoad();
                await Cmd.exec('create_layer', { id: 'well_design_stock_1', type: 'well_design_stock' });
                await updateWells(projectIds, false);
                await updateVisibility(selectedProjectFonds ?? []);

                // GridParamsLoader
                // TODO: ARM-3214 Данный функционал требует дополнительного UI, который будет получать данные с уже настроенными параметрами
                // const gridParams = await api.mapGeneration.fetchGridParams(state.field);
                // console.log(gridParams);

                // TODO: Данный эндпоинт не поддерживает функционал для нескольких месторождений, скважин, площадей и т.д.
                // VoronoiLoader
                // const rawVoronoi = await api.mapGeneration.fetchVoronoiCBOR(
                //     state.field,
                //     state.stratums,
                //     state.versions,
                //     0,
                //     true
                // );
                // await Cmd.parseStreamCBOR('parse_voronoi_cbor', rawVoronoi);
                // Cmd.exec('update_plot');
                // onVoronoiLoad();

                await setTimeInterval();
            };

            init();
        }
    }, [$state.appendNewField]);

    useEffect(() => {
        if (state.dateToLoad !== undefined) {
            const init = async date => {
                state.wellTransitLoaded = false;
                const [dateFrom, dateTo] = getMonthTimestamps(date);

                const rawDebitZIP = await api.mapGeneration.fetchProductionZIP_v2({
                    ...state.requestWells,
                    versions: state.versions,
                    minDate: Math.floor(dateFrom / 1000),
                    maxDate: Math.floor(dateTo / 1000),
                    cbor: true,
                    chunkSize: 10,
                    accum: false,
                });
                const debitFiles = await Cmd.unZIP(rawDebitZIP);

                const debitTasks = debitFiles.map(
                    f =>
                        new Promise(resolve => {
                            requestIdleCallback(
                                () => {
                                    Cmd.parseFilesCBOR('parse_map_production_cbor', f._data.compressedContent);
                                    resolve();
                                },
                                { timeout: 16 },
                            );
                        }),
                );
                console.log(performance.now());
                await Promise.all(debitTasks);
                console.log(performance.now());
                await Cmd.exec('update_plot');

                const rawTransitZIPold = await api.mapGeneration.fetchProductionTransitZIPold({
                    ...state.requestWells,
                    versions: state.versions,
                    minDate: Math.floor(dateFrom / 1000),
                    maxDate: Math.floor(dateTo / 1000),
                    cbor: true,
                });
                const transitFilesOld = await Cmd.unZIP(rawTransitZIPold);
                for (let i = 0; i < transitFilesOld.length; ++i) {
                    requestIdleCallback(
                        async () =>
                            await Cmd.parseFilesCBOR(
                                'parse_well_transit_cbor',
                                transitFilesOld[i]._data.compressedContent,
                            ),
                        { timeout: 16 },
                    );
                }

                const rawTransitZIP = await api.mapGeneration.fetchProductionTransitZIP({
                    ...state.requestWells,
                    versions: state.versions,
                    minDate: Math.floor(dateFrom / 1000),
                    maxDate: Math.floor(dateTo / 1000),
                    cbor: true,
                });
                const transitFiles = await Cmd.unZIP(rawTransitZIP);
                for (let i = 0; i < transitFiles.length; ++i) {
                    requestIdleCallback(
                        async () =>
                            await Cmd.parseFilesCBOR(
                                'parse_well_transit_v3_cbor',
                                transitFiles[i]._data.compressedContent,
                            ),
                        { timeout: 16 },
                    );
                }
                await Cmd.exec('update_plot');
                await onTransitLoad();

                state.loadedDate = [...state.loadedDate, date];
                state.datesOnLoad = state.datesOnLoad.filter(item => item.getTime() !== date.getTime());
            };

            init(new Date(state.dateToLoad)).then(() => {
                state.wellTransitLoaded = true;
            });
            state.dateToLoad = undefined;
        }
    }, [$state.dateToLoad]);

    useEffect(() => {
        if (state.productionDateToLoad !== undefined) {
            const init = async date => {
                const [dateFrom, dateTo] = getMonthTimestamps(date);

                const rawDebitZIP = await api.mapGeneration.fetchProductionZIP_v2({
                    ...state.requestWells,
                    versions: state.versions,
                    minDate: Math.floor(dateFrom / 1000),
                    maxDate: Math.floor(dateTo / 1000),
                    cbor: true,
                    chunkSize: 10,
                    accum: true,
                });
                const debitFiles = await Cmd.unZIP(rawDebitZIP);

                for (let i = 0; i < debitFiles.length; ++i) {
                    requestIdleCallback(
                        async () =>
                            await Cmd.parseFilesCBOR(
                                'parse_map_production_cbor',
                                debitFiles[i]._data.compressedContent,
                            ),
                        { timeout: 16 },
                    );
                }
                requestIdleCallback(async () => await Cmd.exec('update_plot'), {
                    timeout: 32,
                });

                state.productionLoadedDate = [...state.productionLoadedDate, date];
                state.productionDatesOnLoad = state.productionDatesOnLoad.filter(
                    item => item.getTime() !== date.getTime(),
                );
            };

            init(new Date(state.productionDateToLoad));
            state.productionDateToLoad = undefined;
        }
    }, [$state.productionDateToLoad]);

    const setTimeInterval = async () => {
        const globalInterval = await Cmd.get('global_time_interval');
        await Cmd.exec('update_plot');

        const [dateFrom_globalInterval, dateTo_globalInterval] = globalInterval.split(';');

        state.minDate = new Date(Number(`${dateFrom_globalInterval}000`));
        const maxDate = new Date(Number(`${dateTo_globalInterval}000`));
        state.maxDate = maxDate;

        await setCurrentDate(new Date(maxDate.getFullYear(), maxDate.getMonth(), 1));

        baseProgress.setPercent(100);
        baseProgress.setLoading(false);

        coordProgress.setPercent(100);
        coordProgress.setLoading(false);

        gtmProgress.setPercent(100);
        gtmProgress.setLoading(false);

        transitProgress.setPercent(100);
        transitProgress.setLoading(false);

        stateProgress.setPercent(100);
        stateProgress.setLoading(false);

        debitProgress.setPercent(100);
        debitProgress.setLoading(false);

        voronoiProgress.setPercent(100);
        voronoiProgress.setLoading(false);
    };

    const onWellsLoad = () => {
        state.fields.forEach(async ({ id: field }) => {
            const wellsList = await Cmd.get(`field_wells_list:${field}`);
            const parsedWellsList = JSON.parse(wellsList);
            parsedWellsList.wells.forEach(item => state.wells.set(item.well_id, item.well_name));
            state.wellBoreGeometry = Object.entries(parsedWellsList.wellBoreGeometryTypes).map(([id, title]) => ({
                id: Number(id),
                title,
            }));
            state.wellBoreType = Object.entries(parsedWellsList.wellBoreTypes).map(([id, title]) => ({
                id: Number(id),
                title,
            }));

            setPatternLoaded('well_base');
            setPatternLoaded('pad');
            await Cmd.exec('set_debit_version_priority', {
                fieldId: field,
                versions: state.versions,
            });
        });
    };

    const onUserCircleLoad = () => {
        setPatternLoaded('user_circles');
    };

    const onProductionLoad = async () => {
        setPatternLoaded('well_debit');
        setPatternLoaded('well_debit_plot');
        await Cmd.exec('set_current_debit_type', state.versions[0]);
        await Cmd.exec('update_plot');
    };

    const onGtmLoad = async () => {
        const data = await Cmd.get('unique_gtm_list');
        const parsedData = JSON.parse(data);
        if (Array.isArray(parsedData)) {
            const newGtmTypes = parsedData
                .sort((a, b) => a.gtm_id - b.gtm_id)
                .map(({ default_icon_id, gtm_id, gtm_title }) => ({
                    id: gtm_id,
                    title: gtm_title,
                    defaultIconId: default_icon_id,
                }));
            state.gtmTypes = newGtmTypes;
        }
        setPatternLoaded('well_gtm');
    };

    const onTransitLoad = async () => {
        const stratumListRaw = await Cmd.get_v2({
            type: 'layer_data',
            id: 'well_transit_v3',
            data_type: 'stratums',
        });
        let stratumList = null;
        try {
            if (stratumListRaw.trim().startsWith('{') || stratumListRaw.trim().startsWith('[')) {
                stratumList = JSON.parse(stratumListRaw);
                // Дополнительная проверка что данные действительно являются массивом
                if (!Array.isArray(stratumList)) {
                    console.warn('Ожидался массив данных, получено:', typeof stratumList);
                    stratumList = null;
                }
            } else {
                console.warn(`Ответ не является JSON: ${stratumListRaw}`);
            }
        } catch (error) {
            console.warn('Ошибка при парсинге JSON:', error);
            stratumList = null;
        }
        if (Array.isArray(stratumList)) {
            const newStratumLegwndTransit = {};
            stratumList.forEach(stratum => {
                newStratumLegwndTransit[stratum.stratum_id.toString()] = stratum.stratum_name;
            });
            state.stratumsLegendTransit = newStratumLegwndTransit;
        }
        setPatternLoaded('well_transit_v3');
    };

    const onStatesLoad = () => {
        setPatternLoaded('well_state');
    };

    const onContourLoad = async () => {
        const data = await Cmd.get('contour_list');
        const parsedData = JSON.parse(data);
        if (Array.isArray(parsedData)) {
            const newContourData = parsedData.map(data => ({ ...data }));
            state.contourData = newContourData;
        }
        setPatternLoaded('contour');

        // TODO: fix it.
        // const data1 = Cmd.get('unique_strata_list');

        const stratumListRaw = await Cmd.get_v2({
            type: 'strata_scheme',
        });
        const stratumList = JSON.parse(stratumListRaw);
        const stratumsListWithKeys = stratumList.map(stratum => ({
            ...stratum,
            key: `${stratum.field_id}-${stratum.stratum_id}`,
        }));
        if (Array.isArray(stratumsListWithKeys)) {
            const newVoronoiData = stratumsListWithKeys.map(data => ({ ...data })).sort((a, b) => a - b);
            const sortedVoronoiData = newVoronoiData.sort((a, b) =>
                a.stratum_name.toLowerCase().localeCompare(b.stratum_name.toLowerCase()),
            );

            const combined = [...state.uniqueStrataList, ...sortedVoronoiData];

            const uniqueMap = new Map();

            combined.forEach(item => {
                uniqueMap.set(item.key, item);
            });

            state.uniqueStrataList = Array.from(uniqueMap.values());
        }

        setPatternLoaded('voronoi');
    };

    const resetMapState = () => {
        // Сброс состояния карты
        baseProgress.setPercent(0);
        baseProgress.setLoading(false);
        coordProgress.setPercent(0);
        coordProgress.setLoading(false);
        gtmProgress.setPercent(0);
        gtmProgress.setLoading(false);
        transitProgress.setPercent(0);
        transitProgress.setLoading(false);
        stateProgress.setPercent(0);
        stateProgress.setLoading(false);
        debitProgress.setPercent(0);
        debitProgress.setLoading(false);
        voronoiProgress.setPercent(0);
        voronoiProgress.setLoading(false);

        state.wells = new Map();
        state.wellBoreGeometry = [];
        state.wellBoreType = [];
        state.gtmTypes = [];
        state.stratumsLegendTransit = {};
        state.contourData = [];
        state.uniqueStrataList = [];

        state.loadedLayersData.clear();
        state.layerVisibility.clear();
        state.layerStructure.clear();
        state.layerSettings.clear();
        state.selectedWells = [];
        state.checkedSelectedWells = {};

        state.loadedDate = [];
        state.datesOnLoad = [];
        state.productionLoadedDate = [];
        state.productionDatesOnLoad = [];

        state.appendNewField = false;

        if (window.Cmd) {
            Cmd.exec('reset_plot').catch(err => console.warn('Не удалось сбросить plot', err));
        }
    };

    useEffect(() => {
        return () => {
            resetMapState();
        };
    }, [pathname]);

    return (
        <div className="relative h-full w-full">
            {loading && (
                <Spin
                    spinning={loading}
                    className="absolute inset-0 !flex h-full w-full flex-col items-center justify-center bg-white dark:bg-block-secondary"
                />
            )}

            {
                // $baseProgress.loading ||
                $coordProgress.loading && (
                    // $gtmProgress.loading ||
                    // $transitProgress.loading ||
                    // $stateProgress.loading ||
                    // $debitProgress.loading ||
                    // $voronoiProgress.loading) && (
                    <Spin
                        spinning={
                            $baseProgress.loading ||
                            $gtmProgress.loading ||
                            $transitProgress.loading ||
                            $stateProgress.loading ||
                            $debitProgress.loading ||
                            $voronoiProgress.loading
                        }
                        className="absolute inset-0 !flex h-full w-full flex-col items-center justify-center bg-white/80 dark:bg-block-secondary/80"
                    />
                )
            }

            <div className="h-full w-full">
                <div className="relative flex h-full w-full flex-col">
                    <div className="relative flex h-full w-full">
                        <canvas
                            id="scene"
                            className="absolute inset-0 h-full w-full"
                            onContextMenu={e => e.preventDefault()}
                        ></canvas>
                    </div>
                </div>
            </div>
        </div>
    );
};
